import { MessageSquare, Trash2 } from "lucide-react";
import type { MouseEvent } from "react";
import type { ConversationSummary } from "./session-history-utils";
import { formatRelativeDate } from "./session-history-utils";
import { FintheonNumericText } from "../numbers/FintheonNumericText";

interface SessionGroupProps {
  label: string;
  sessions: ConversationSummary[];
  startIndex: number;
  selectedIndex: number;
  currentConversationId?: string;
  setSelectedIndex: (index: number) => void;
  onSelectSession: (id: string) => void;
  onDeleteSession: (id: string, event: MouseEvent) => void;
}

export function SessionGroup({
  label,
  sessions,
  startIndex,
  selectedIndex,
  currentConversationId,
  setSelectedIndex,
  onSelectSession,
  onDeleteSession,
}: SessionGroupProps) {
  return (
    <div>
      <div className="px-3 py-1 text-[9px] uppercase tracking-[0.18em] text-[var(--fintheon-accent)]/70">
        {label}
      </div>
      {sessions.map((session, index) => (
        <SessionRow
          key={session.id}
          session={session}
          index={startIndex + index}
          isSelected={startIndex + index === selectedIndex}
          isActive={session.id === currentConversationId}
          setSelectedIndex={setSelectedIndex}
          onSelectSession={onSelectSession}
          onDeleteSession={onDeleteSession}
        />
      ))}
    </div>
  );
}

export function EmptyState({ search }: { search: string }) {
  return (
    <div className="flex min-h-32 flex-col items-center justify-center px-3 py-7 text-center">
      <MessageSquare
        size={16}
        className="mb-1.5 text-[var(--fintheon-muted)]"
      />
      <p className="text-[11px] text-[var(--fintheon-muted)]">
        {search ? "No matching conversations" : "No conversations yet"}
      </p>
      {!search ? (
        <p className="mt-0.5 text-[9px] text-[var(--fintheon-muted)]/55">
          Start chatting to create your first session
        </p>
      ) : null}
    </div>
  );
}

function SessionRow({
  session,
  index,
  isSelected,
  isActive,
  setSelectedIndex,
  onSelectSession,
  onDeleteSession,
}: {
  session: ConversationSummary;
  index: number;
  isSelected: boolean;
  isActive: boolean;
  setSelectedIndex: (index: number) => void;
  onSelectSession: (id: string) => void;
  onDeleteSession: (id: string, event: MouseEvent) => void;
}) {
  return (
    <div className="group/item relative px-2">
      <div className="flex items-center gap-2 py-1.5">
        <button
          type="button"
          data-index={index}
          onClick={() => onSelectSession(session.id)}
          onMouseEnter={() => setSelectedIndex(index)}
          className="min-w-0 flex-1 text-left"
        >
          <span className="flex min-w-0 items-center justify-between gap-2">
            <span
              className="min-w-0 truncate text-[11px] font-medium"
              style={{
                color: isActive
                  ? "var(--fintheon-accent)"
                  : "var(--fintheon-text)",
              }}
            >
              {session.title}
            </span>
            <span className="shrink-0 text-[9px] tabular-nums text-[var(--fintheon-muted)]/55">
              <FintheonNumericText
                text={formatRelativeDate(session.lastMessageAt)}
                kind="date-time"
              />
            </span>
          </span>
        </button>
        <button
          type="button"
          onClick={(event) => onDeleteSession(session.id, event)}
          className="grid h-5 w-5 shrink-0 place-items-center rounded-full text-[var(--fintheon-muted)]/45 opacity-0 transition group-hover/item:opacity-100 hover:text-red-300"
          title="Delete permanently"
          aria-label={`Delete ${session.title} permanently`}
        >
          <Trash2 size={10} />
        </button>
      </div>
      <FadingRule active={isActive || isSelected} />
    </div>
  );
}

function FadingRule({ active = false }: { active?: boolean }) {
  return (
    <div
      className={`h-px ${active ? "bg-[var(--fintheon-accent)]/24" : "bg-[var(--fintheon-accent)]/12"}`}
      style={{
        maskImage:
          "linear-gradient(to right, transparent, black 16%, black 84%, transparent)",
      }}
    />
  );
}
